// Пречките пред записа на инвентаризация — чиста функция (футърът показва
// първата, Ctrl/Cmd+Enter я казва в съобщение и, ако тя сочи клетка, фокусира
// клетката). Редът е важен: склад → дата → приготвените рецепти (порции,
// рецепта без съставки, съставка без насипно преброено — `buildRecipeIssues` в
// stocktakeRecipes.ts) → нищо преброено → ед. цена на излишък без източник →
// партида/срок на новите партиди.
//
// Ед. цената: излишък без последна доставна цена в склада и без написана цена
// би влязъл в наличността на 0 (първата инвентаризация на нов обект) и би се
// преоценил едва от следваща доставка — затова е ЗАДЪЛЖИТЕЛНА за тези редове
// (`priceMissing` в `useStocktakeRowsActions`). Редовете с известна цена не се
// засягат.
import type { TFunction } from "i18next";
import { STOCKTAKE_COLS, type StocktakeComputedRow } from "./stocktakeWorksheetShared";

// Колоната „Ед. цена" в решетката
const PRICE_COL = STOCKTAKE_COLS.price;
// Колко имена изброява пречката за цена, преди „и още N"
const LISTED_NAMES = 3;

export type StocktakeIssue = {
  text: string;
  // клетката, която Ctrl/Cmd+Enter фокусира (редът се разкрива, ако е скрит)
  focus?: { productId: number; col: number };
  // … или полето „Порции" на приготвена рецепта
  focusRecipeId?: number;
};

type Params = {
  warehouseId: number | null;
  date: string;
  countedRows: StocktakeComputedRow[];
  t: TFunction;
  // пречките на приготвените рецепти (`buildRecipeIssues`)
  recipeIssues?: StocktakeIssue[];
};

export function buildStocktakeIssues({
  warehouseId,
  date,
  countedRows,
  t,
  recipeIssues = [],
}: Params): StocktakeIssue[] {
  const issues: StocktakeIssue[] = [];
  if (!warehouseId) issues.push({ text: t("inventoryPage.errWarehouse") });
  if (!date) issues.push({ text: t("inventoryPage.errDate") });
  issues.push(...recipeIssues);
  if (countedRows.length === 0) issues.push({ text: t("inventoryPage.errNoCounts") });

  const missingPrice = countedRows.filter((row) => row.priceMissing);
  if (missingPrice.length) {
    const names = missingPrice.slice(0, LISTED_NAMES).map((row) => row.product.name).join(", ");
    const more = missingPrice.length - LISTED_NAMES;
    issues.push({
      text: t("inventoryPage.stkIssuePrice", {
        products: more > 0 ? `${names} ${t("inventoryPage.stkIssueMore", { count: more })}` : names,
      }),
      focus: { productId: missingPrice[0].product.id, col: PRICE_COL },
    });
  }

  const seen = new Set<string>();
  for (const row of countedRows) {
    if (row.kind !== "surplus") continue;
    const texts = [
      row.product.trackBatch && !row.draft.batch.trim()
        ? t("inventoryPage.issueBatch", { product: row.product.name })
        : null,
      row.product.trackExpiry && !row.draft.expiry
        ? t("inventoryPage.issueExpiry", { product: row.product.name })
        : null,
    ];
    for (const text of texts) {
      if (!text || seen.has(text)) continue;
      seen.add(text);
      issues.push({ text });
    }
  }
  return issues;
}
